import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createInconsistentReadingsTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is ${userFullname} with Tellihealth.
          <br />
          I'm calling to help you get started with your Remote Patient
          Monitoring program.
          <br />
          For quality and compliance purposes, this call may be recorded.
          <br />
          To protect your privacy, can you please confirm your full name and
          date of birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'readingAdherenceBarrierCheck',
      type: 'radio',
      title: <p>Reading Adherence & Barrier Check</p>,
      description: (
        <ScriptWrapper>
          "I’m calling because we noticed some inconsistent readings coming
          through." "Have you been able to take and send your readings
          consistently, or has anything been getting in the way?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Consistent readings',
          value: 'consistentReadings',
        },
        {
          key: makeid(10),
          label: 'Inconsistent readings',
          value: 'inconsistentReadings',
        },
        {
          key: makeid(10),
          label: 'No readings',
          value: 'noReadings',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  address it."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Other',
          value: 'other',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'textarea',
              name: 'readingAdherenceCheckOther',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Barriers identified',
          value: 'barriersIdentified',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  support you."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              name: 'barriersList',
              type: 'checkbox',
              title: <p>Barriers</p>,
              description: '',
              required: false,
              closable: true,
              options: [
                {
                  key: makeid(10),
                  label: 'Device',
                  value: 'device',
                },
                {
                  key: makeid(10),
                  label: 'Supplies',
                  value: 'supplies',
                },
                {
                  key: makeid(10),
                  label: 'Schedule',
                  value: 'schedule',
                },
                {
                  key: makeid(10),
                  label: 'Education',
                  value: 'education',
                },
                {
                  key: makeid(10),
                  label: 'Connectivity',
                  value: 'connectivity',
                },
                {
                  key: makeid(10),
                  label: 'Power/charging',
                  value: 'powerCharging',
                },
                {
                  key: makeid(10),
                  label: 'Other',
                  value: 'other',
                  hideSubitemsIfParentNotSelected: true,
                  subItems: [
                    {
                      key: makeid(10),
                      type: 'textarea',
                      name: 'barriersListOther',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'inconsistentReadingsIdentified',
      type: 'checkbox',
      title: <p>Inconsistent Readings Discussion</p>,
      description: (
        <ScriptWrapper>
          "When readings vary more than expected, it can sometimes be related to
          how, when, or where they're taken."
          <br />
          "I'd like to review a few common things that can affect readings."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Variation outside expected range',
          value: 'variationOutsideExpectedRange',
        },
        {
          key: makeid(10),
          label: 'Transmission issues noted',
          value: 'transmissionIssuesNoted',
        },
        {
          key: makeid(10),
          label: 'Patient reports difficulty taking readings',
          value: 'patientReportsDifficultyTakingReadings',
        },
        {
          key: makeid(10),
          label: 'Trend concern noted',
          value: 'trendConcernNoted',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'techniqueTimingDeviceUsageReview',
      type: 'checkbox',
      title: <p>Technique, Timing & Device Usage Review</p>,
      description: (
        <ScriptWrapper>
          "Let's walk through a few areas together."
          <ul>
            <li>– Time of day readings are taken</li>
            <li>– Resting before the reading</li>
            <li>– Body position during the reading</li>
            <li>– Proper cuff or device placement</li>
            <li>– Recent activity, stress, or pain</li>
            <li>– Ensuring the device is charged or powered on</li>
            <li>
              – Where the device is located when sending readings (for example,
              avoiding areas with poor signal, metal walls, or metal roofing
              that can interfere with transmission)
            </li>
          </ul>
          "Have any of these been different recently?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Time of day reviewed',
          value: 'timeOfDayReviewed',
        },
        {
          key: makeid(10),
          label: 'Resting prior to reading reviewed',
          value: 'restingPriorToReadingReviewed',
        },
        {
          key: makeid(10),
          label: 'Positioning reviewed',
          value: 'positioningReviewed',
        },
        {
          key: makeid(10),
          label: 'Device placement reviewed',
          value: 'devicePlacementReviewed',
        },
        {
          key: makeid(10),
          label: 'Activity/stress discussed',
          value: 'activityStressDiscussed',
        },
        {
          key: makeid(10),
          label: 'Device power/charging reviewed',
          value: 'devicePowerChargingReviewed',
        },
        {
          key: makeid(10),
          label: 'Signal/location reviewed',
          value: 'signalLocationReviewed',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'reReadTransmissionCheck',
      type: 'radio',
      title: <p>Re-Read & Transmission Check</p>,
      description: (
        <ScriptWrapper>
          "If you're able, let's try taking another reading following these
          steps and make sure the device is powered on and able to send the
          reading." (Guide patient through re-read if appropriate.)
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Re-read performed and transmitted',
          value: 'reReadPerformedAndTransmitted',
        },
        {
          key: makeid(10),
          label: 'Re-read performed but not transmitted',
          value: 'reReadPerformedButNotTransmitted',
        },
        {
          key: makeid(10),
          label: 'Patient declined re-read',
          value: 'patientDeclinedReRead',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'outcomeEscalation',
      type: 'checkbox',
      title: <p>Inconsistent Readings Outcome & Next Steps</p>,
      description: (
        <ScriptWrapper>
          If readings appear more consistent and transmit successfully: "Thank
          you — those readings look more consistent."
          <br />
          If inconsistency or transmission issues continue: "Thank you for
          checking that with me. I'm going to document what we reviewed and
          notify your clinic so they can review the trends and determine next
          steps."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Readings improved',
          value: 'readingsImproved',
        },
        {
          key: makeid(10),
          label: 'Inconsistency persists',
          value: 'inconsistencyPersists',
        },
        {
          key: makeid(10),
          label: 'Transmission issues persist',
          value: 'transmissionIssuesPersist',
        },
        {
          key: makeid(10),
          label: 'Clinic notified for review',
          value: 'clinicNotifiedForReview',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we have on file for you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Which plan is listed?"
                  <br />
                  "Thank you — I'll confirm what's documented in your chart."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              type: 'checkbox',
              name: 'insuranceTypeOnFile',
              options: [
                {
                  key: makeid(10),
                  label: 'Insurance type on file confirmed',
                  value: 'insuranceTypeOnFileConfirmed',
                },
              ],
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "And what's the best cell phone number to reach you? Is [number on
          file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone confirmed',
          value: 'cellPhoneConfirmed',
        },
        {
          key: makeid(10),
          label: 'Updated',
          value: 'updated',
        },
        {
          key: makeid(10),
          label: 'No cell phone',
          value: 'noCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          If patient has a cell phone: "We also offer text messages for
          reminders and updates related to your RPM care. Would you like to
          receive text messages from us?"
          <br />
          If yes: enroll. If no: document decline. If no cell phone: do not
          offer texting.
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered/Accepted',
          value: 'offeredAccepted',
        },
        {
          key: makeid(10),
          label: 'Offered/Declined',
          value: 'offeredDeclined',
        },
        {
          key: makeid(10),
          label: 'Not eligible',
          value: 'notEligible',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Scheduling</p>,
      description: (
        <ScriptWrapper>
          “As a reminder, these monthly phone calls are required to remain
          active in the RPM program.”
          <br />
          “They help us stay connected and identify issues early.”
          <br />
          “Would you like to schedule your next monthly check-in now, or should
          we call you closer to the date?”
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Monthly phone call requirement reviewed',
          value: 'monthlyPhoneCallRequirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Program monitoring process explained',
          value: 'programMonitoringProcessExplained',
        },
        {
          key: makeid(10),
          label: 'Nurse or clinic outreach for out-of-range readings explained',
          value: 'nurseOrClinicOutreachForOutOfRangeReadingsExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand everything we discussed
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TInconsistentReadingsTemplateValues = ExtractStepValues<
  ReturnType<typeof createInconsistentReadingsTemplate>
>;

export const createInconsistentReadingsBlurb = (
  data: TInconsistentReadingsTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    readingAdherenceBarrierCheck,
    readingAdherenceCheckOther,
    barriersList,
    barriersListOther,
    inconsistentReadingsIdentified,
    techniqueTimingDeviceUsageReview,
    reReadTransmissionCheck,
    outcomeEscalation,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;
  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    readingAdherenceBarrierCheck &&
    !hasField(ctx, 'readingAdherenceBarrierCheck')
  ) {
    addField(ctx, 'readingAdherenceBarrierCheck');
    const map = {
      consistentReadings: 'consistent readings',
      inconsistentReadings: 'inconsistent readings',
      missedReadings: 'missed readings',
      noReadings: 'no readings',
      barriersIdentified: 'barriers identified',
      other: readingAdherenceCheckOther,
    };

    const value = map[readingAdherenceBarrierCheck];

    if (value) {
      txt.push(`Reading adherence reviewed. Patient reports ${value}.`);
    }

    if (
      readingAdherenceBarrierCheck === 'barriersIdentified' &&
      barriersList?.length &&
      !hasField(ctx, 'barriersList')
    ) {
      addField(ctx, 'barriersList');
      const barriersMap = {
        device: 'device issues',
        supplies: 'supply availability',
        schedule: 'schedule conflicts',
        education: 'education needs',
        connectivity: 'connectivity issues',
        powerCharging: 'power or charging issues',
        other: barriersListOther,
      };
      const items = mapValuesToText(barriersList, barriersMap);
      if (items.length) {
        txt.push(
          `Barriers identified and documented, including ${formatList(items)}.`,
        );
      }
    }
  }

  if (
    inconsistentReadingsIdentified?.length &&
    !hasField(ctx, 'inconsistentReadingsIdentified')
  ) {
    addField(ctx, 'inconsistentReadingsIdentified');
    const map = {
      variationOutsideExpectedRange: 'variation outside expected range',
      transmissionIssuesNoted: 'transmission issues noted',
      patientReportsDifficultyTakingReadings:
        'patient reports difficulty taking readings',
      trendConcernNoted: 'trend concern noted',
    };
    const items = mapValuesToText(inconsistentReadingsIdentified, map);

    if (items.length) {
      txt.push(
        `Inconsistent readings identified and discussed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    techniqueTimingDeviceUsageReview?.length &&
    !hasField(ctx, 'techniqueTimingDeviceUsageReview')
  ) {
    addField(ctx, 'techniqueTimingDeviceUsageReview');
    const map = {
      timeOfDayReviewed: 'time of day reviewed',
      restingPriorToReadingReviewed: 'resting prior to reading reviewed',
      positioningReviewed: 'positioning reviewed',
      devicePlacementReviewed: 'device placement reviewed',
      activityStressDiscussed: 'activity and stress impact discussed',
      devicePowerChargingReviewed: 'device power and charging reviewed',
      signalLocationReviewed: 'signal/location considerations reviewed',
    };
    const items = mapValuesToText(techniqueTimingDeviceUsageReview, map);

    if (items.length) {
      txt.push(
        `Technique, timing, and device usage reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (reReadTransmissionCheck && !hasField(ctx, 'reReadTransmissionCheck')) {
    addField(ctx, 'reReadTransmissionCheck');
    const map = {
      reReadPerformedAndTransmitted:
        're-read performed and transmission successful',
      reReadPerformedButNotTransmitted:
        're-read performed but transmission unsuccessful',
      patientDeclinedReRead: 'patient declined re-read',
    };

    txt.push(`Re-read status: ${map[reReadTransmissionCheck]}.`);
  }

  if (outcomeEscalation?.length && !hasField(ctx, 'outcomeEscalation')) {
    addField(ctx, 'outcomeEscalation');
    const map = {
      readingsImproved: 'readings improved',
      inconsistencyPersists: 'inconsistency persists',
      transmissionIssuesPersist: 'transmission issues persist',
      clinicNotifiedForReview: 'clinic notified for further review',
    };
    const items = mapValuesToText(outcomeEscalation, map);

    if (items.length) {
      txt.push(`Outcome documented, including ${formatList(items)}.`);
    }
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare Advantage (plan selected)',
      commercial: 'Commercial',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]} based on information on file.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneConfirmed: 'confirmed',
      updated: 'updated',
      noCellPhone: 'no cell phone',
    };
    txt.push(
      `Cell phone contact reviewed. Number ${map[cellPhoneVerification]}. `,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAccepted: 'offered and accepted',
      offeredDeclined: 'offered and declined',
      notEligible: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      monthlyPhoneCallRequirementReviewed: 'monthly phone call requirement',
      programMonitoringProcessExplained: 'program monitoring process explained',
      nurseOrClinicOutreachForOutOfRangeReadingsExplained:
        'that out-of-range readings may result in nurse or clinic outreach',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);

    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`First monthly check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join();
};
